import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import './eb-sport-card.js';

export class SportCollection extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.closeSizeGuides();
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.video = this.querySelector('video');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = matchMedia('(max-width: 749px)');
    this.userPaused = false;
    this.visible = true;
    this.dataset.enhanced = '';
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const guideLink = event.target.closest('[data-guide-open]');
      if (guideLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        if (this.openSizeGuide(guideLink)) event.preventDefault();
      }
      if (event.target.closest('[data-guide-close]')) this.closeSizeGuides();
      const zoomLink = event.target.closest('[data-guide-zoom]');
      if (zoomLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        const dialog = zoomLink.closest('[data-size-guide]');
        this.setGuideZoom(dialog, !dialog.hasAttribute('data-zoomed'));
      }
      const filter = event.target.closest('[data-piece-filter]');
      if (filter && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        const url = new URL(location.href);
        if (filter.dataset.pieceFilter === 'all') url.searchParams.delete('tip');
        else url.searchParams.set('tip', filter.dataset.pieceFilter);
        history.pushState(null, '', url);
        this.filter();
      }
      const color = event.target.closest('[data-color-filter]');
      if (color) this.setFilter('culoare', color.dataset.colorFilter);
      if (event.target.closest('[data-video-stop]')) {
        this.userPaused = true;
        this.syncVideo();
        this.querySelector('.ebsc__hero-link')?.focus({ preventScroll: true });
      }
    }, { signal });
    this.querySelector('[data-range-filter]')?.addEventListener('change', event => {
      this.setFilter('gama', event.target.value);
    }, { signal });
    this.querySelectorAll('[data-size-guide]').forEach(dialog => {
      dialog.addEventListener('close', () => {
        unlockScroll(dialog);
        if (this.guideTrigger?.isConnected) this.guideTrigger.focus({ preventScroll: true });
        this.guideTrigger = null;
      }, { signal });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, { signal });
    });
    window.addEventListener('popstate', () => this.filter(), { signal });
    document.addEventListener('visibilitychange', () => this.syncVideo(), { signal });
    this.motion.addEventListener('change', () => this.syncVideo(), { signal });
    this.mobile.addEventListener('change', () => this.setVideoSource(), { signal });
    this.video?.addEventListener('play', () => this.syncVideoButton(), { signal });
    this.video?.addEventListener('pause', () => this.syncVideoButton(), { signal });
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.syncVideo();
    }, { threshold: 0.05 });
    if (this.video) this.observer.observe(this.video);
    this.filter();
    queueMicrotask(() => { if (this.isConnected) this.filter(); });
    this.syncVideo();
  }

  openSizeGuide(trigger) {
    const piece = trigger.dataset.guideOpen;
    if (!['bustiera', 'colanti', 'sorti'].includes(piece)) return false;
    const dialog = this.querySelector(`[data-size-guide="${piece}"]`);
    if (!dialog?.showModal) return false;
    this.guideTrigger = trigger;
    if (!dialog.open) {
      this.setGuideZoom(dialog, false);
      dialog.showModal();
      lockScroll(dialog);
      dialog.querySelector('[data-guide-close]')?.focus({ preventScroll: true });
    }
    return true;
  }

  closeSizeGuides() {
    this.querySelectorAll('[data-size-guide]').forEach(dialog => {
      if (dialog.open) dialog.close();
      unlockScroll(dialog);
    });
  }

  setGuideZoom(dialog, enlarged) {
    dialog.toggleAttribute('data-zoomed', enlarged);
    const link = dialog.querySelector('[data-guide-zoom]');
    const text = enlarged ? dialog.dataset.collapseLabel : dialog.dataset.expandLabel;
    link?.setAttribute('aria-expanded', String(enlarged));
    link?.setAttribute('aria-label', text);
    const label = dialog.querySelector('[data-guide-zoom-label]');
    if (label) label.textContent = text;
  }

  setFilter(key, value) {
    const url = new URL(location.href);
    if (value === 'all') url.searchParams.delete(key);
    else url.searchParams.set(key, value);
    history.pushState(null, '', url);
    this.filter();
  }

  filter() {
    const params = new URL(location.href).searchParams;
    const requested = params.get('tip');
    const piece = ['bustiera', 'colanti', 'sorti'].includes(requested) ? requested : 'all';
    const range = ['zenara', 'bootylift', 'hypersculpt'].includes(params.get('gama')) ? params.get('gama') : 'all';
    const color = ['bordo', 'maro', 'gri', 'albastru', 'menta', 'negru'].includes(params.get('culoare')) ? params.get('culoare') : 'all';
    const normalizeColor = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    this.toggleAttribute('data-filtered', piece !== 'all');
    let count = 0;
    this.querySelectorAll('[data-sport-card]').forEach(card => {
      const colorInput = color !== 'all' ? Array.from(card.querySelectorAll('[data-color]')).find(input => normalizeColor(input.value) === color) : null;
      card.hidden = (piece !== 'all' && card.dataset.piece !== piece) || (range !== 'all' && card.dataset.range !== range) || (color !== 'all' && !colorInput);
      if (colorInput && card.hasAttribute('data-ready') && card.dataset.appliedColor !== color) {
        card.dataset.appliedColor = color;
        colorInput.checked = true;
        colorInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (color === 'all') delete card.dataset.appliedColor;
      if (!card.hidden) count++;
    });
    const rangeFilter = this.querySelector('[data-range-filter]');
    if (rangeFilter) rangeFilter.value = range;
    this.querySelectorAll('[data-color-filter]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.colorFilter === color));
    });
    this.querySelectorAll('[data-piece-filter]').forEach(link => {
      if (link.dataset.pieceFilter === piece) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    this.querySelectorAll('[data-range-group]').forEach(group => {
      group.hidden = !Array.from(group.querySelectorAll('[data-sport-card]')).some(card => !card.hidden);
    });
    const status = this.querySelector('[data-product-count]');
    if (status) status.textContent = this.dataset.countLabel.replace('[count]', String(count));
    const empty = this.querySelector('[data-sport-empty]');
    if (empty) empty.hidden = count > 0;
  }

  setVideoSource() {
    if (!this.video) return;
    const source = this.mobile.matches ? (this.video.dataset.mobile || this.video.dataset.desktop) : this.video.dataset.desktop;
    if (!source || this.video.getAttribute('src') === source) return;
    this.video.src = source;
    this.video.load();
    this.syncVideo();
  }

  syncVideo() {
    if (!this.video) return;
    const avoidAutoplay = this.motion.matches || navigator.connection?.saveData;
    const pause = this.userPaused || document.hidden || !this.visible || avoidAutoplay;
    if (pause) { this.video.pause(); this.syncVideoButton(); return; }
    if (!this.video.getAttribute('src')) {
      this.setVideoSource();
      if (!this.video.getAttribute('src')) return;
    }
    this.video.play().catch(() => this.syncVideoButton());
  }

  syncVideoButton() {
    const paused = this.video?.paused ?? true;
    this.querySelectorAll('[data-video-stop]').forEach(button => { button.hidden = paused; });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closeSizeGuides();
    this.abort?.abort();
    this.observer?.disconnect();
    this.video?.pause();
  }
}

if (!customElements.get('eb-sport-collection')) customElements.define('eb-sport-collection', SportCollection);
